import React, { useState, useEffect } from 'react';
import { Save, MessageCircle, Bell, BellOff, Users, User, CheckSquare, EyeOff } from 'lucide-react';
import api from '../../services/api';

/**
 * CommunicationPreferences - Settings panel for communication hub filters
 */
const CommunicationPreferences = () => {
  const [preferences, setPreferences] = useState({
    hide_unless_tagged: false,
    hide_handled: false,
    only_my_conversations: false,
    only_unresponded: false,
    only_my_assigned_cases: false,
    hide_group_conversations: false,
    exclude_muted: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Load preferences on mount
  useEffect(() => {
    const loadPreferences = async () => {
      try {
        const response = await api.get('/users/preferences/communications');
        setPreferences(response.data);
      } catch (error) {
        // If no preferences exist, use defaults
        if (error.response?.status !== 404) {
          console.error('Error loading communication preferences:', error);
        }
      } finally {
        setLoading(false);
      }
    };

    loadPreferences();
  }, []);

  const handleToggle = (key) => {
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
    setSuccess(''); // Clear success message on change
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      await api.put('/users/preferences/communications', preferences);
      setSuccess('Preferences saved successfully');

      // Store in localStorage as well for immediate frontend use
      localStorage.setItem('comm_preferences', JSON.stringify(preferences));
    } catch (error) {
      console.error('Error saving preferences:', error);
      setError('Failed to save preferences. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const preferenceOptions = [
    {
      key: 'hide_unless_tagged',
      label: 'Hide messages unless I am tagged',
      description: 'Only show conversations where you participated or were @mentioned',
      icon: Bell
    },
    {
      key: 'hide_handled',
      label: 'Hide handled conversations',
      description: 'Hide conversations where another team member has already responded to the client',
      icon: CheckSquare
    },
    {
      key: 'only_my_conversations',
      label: 'Only show my conversations',
      description: 'Only show conversations where you have sent at least one message',
      icon: User
    },
    {
      key: 'only_unresponded',
      label: 'Only show unresponded',
      description: 'Only show conversations where the last message is from the client (awaiting response)',
      icon: MessageCircle
    },
    {
      key: 'only_my_assigned_cases',
      label: 'Only show my assigned cases',
      description: 'Only show communications for cases where you are the assigned attorney',
      icon: Users
    },
    {
      key: 'hide_group_conversations',
      label: 'Hide group conversations',
      description: 'Hide multi-participant group conversations, only show 1:1 client communications',
      icon: EyeOff
    },
    {
      key: 'exclude_muted',
      label: 'Exclude muted conversations',
      description: 'Hide conversations you have muted from the main list',
      icon: BellOff
    }
  ];

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-2xl">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Communication Preferences</h2>
        <p className="mt-1 text-sm text-gray-500">
          Configure which messages and conversations appear in your Communication Hub.
          These filters help reduce noise and focus on what's important to you.
        </p>
      </div>

      <div className="space-y-4">
        {preferenceOptions.map(option => {
          const Icon = option.icon;
          const isEnabled = preferences[option.key];

          return (
            <div
              key={option.key}
              className={`border rounded-lg p-4 transition-colors ${
                isEnabled ? 'border-gray-200 bg-gray-50' : 'border-gray-200 bg-white'
              }`}
            >
              <div className="flex items-start gap-4">
                <div className={`p-2 rounded-lg ${
                  isEnabled ? 'bg-gray-100 text-yellow-600' : 'bg-gray-100 text-gray-500'
                }`}>
                  <Icon className="w-5 h-5" />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <h3 className={`font-medium ${
                      isEnabled ? 'text-gray-900' : 'text-gray-900'
                    }`}>
                      {option.label}
                    </h3>

                    <button
                      type="button"
                      onClick={() => handleToggle(option.key)}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 ${
                        isEnabled ? 'bg-yellow-500' : 'bg-gray-200'
                      }`}
                      role="switch"
                      aria-checked={isEnabled}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          isEnabled ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>

                  <p className={`mt-1 text-sm ${
                    isEnabled ? 'text-yellow-600' : 'text-gray-500'
                  }`}>
                    {option.description}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {success && (
        <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-sm text-green-700">{success}</p>
        </div>
      )}

      {/* Save Button */}
      <div className="mt-6 flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
        >
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              Save Preferences
            </>
          )}
        </button>
      </div>

      {/* Info Note */}
      <div className="mt-6 p-4 bg-gray-50 rounded-lg">
        <h4 className="text-sm font-medium text-gray-900 mb-2">About @Mentions</h4>
        <p className="text-sm text-gray-600">
          You can @mention team members in messages by typing @ followed by their name
          (e.g., @John Smith). When you're mentioned, you'll receive an email notification
          and the message will be highlighted in your Communication Hub.
        </p>
      </div>
    </div>
  );
};

export default CommunicationPreferences;
